<template>
	<nav class="nongmon-red-bg h-24 relative">
		<div class="container mx-auto px-6">
			<div class="flex justify-between items-center h-24">
				<!-- Desktop Navigation -->
				<div class="hidden lg:flex items-center">
					<ul class="flex gap-8 text-white">
						<li>
							<RouterLink
								:to="{ name: 'home', hash: '#about' }"
								class="hover:opacity-75 transition-opacity"
							>
								ABOUT
							</RouterLink>
						</li>
						<li>
							<RouterLink
								:to="{ name: 'home', hash: '#news' }"
								class="hover:opacity-75 transition-opacity hover:underline"
							>
								HOT NEWS
							</RouterLink>
						</li>
						<li>
							<RouterLink
								:to="{ name: 'home', hash: '#must-eat' }"
								class="text-white"
							>
								MUST EAT
							</RouterLink>
						</li>
						<li class="relative group">
							<span
								class="cursor-pointer hover:opacity-75 transition-opacity flex items-center gap-1"
								@click="toggleDesktopDropdown"
								@touchstart="toggleDesktopDropdown"
							>
								FOOD & TRAVEL GUIDE
								<svg
									class="w-4 h-4 transition-transform"
									:class="{ 'rotate-180': isDesktopDropdownOpen }"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 9l-7 7-7-7"
									/>
								</svg>
							</span>
							<div
								class="absolute left-0 mt-0 w-48 transition-all duration-300 ease-in-out"
								:class="{
									'opacity-100 visible translate-y-0': isDesktopDropdownOpen,
									'opacity-0 invisible -translate-y-4': !isDesktopDropdownOpen,
								}"
							>
								<div
									class="nongmon-red-bg backdrop-blur-sm shadow-lg mt-4 py-4"
								>
									<RouterLink
										:to="{ name: 'eat-at', hash: '#guide' }"
										class="block px-4 py-2 text-white hover:bg-white/10 transition-colors"
										@click="closeMenu"
									>
										FOOD GUIDE
									</RouterLink>
									<RouterLink
										:to="{ name: 'articles', hash: '#guide' }"
										class="block px-4 py-2 text-white hover:bg-white/10 transition-colors"
										@click="closeMenu"
									>
										TRAVEL GUIDE
									</RouterLink>
								</div>
							</div>
						</li>
					</ul>
				</div>

				<!-- Mobile Menu Button -->
				<button
					@click="isOpen = !isOpen"
					class="lg:hidden p-2 rounded-md text-white hover:opacity-75"
				>
					<Bars3Icon v-if="!isOpen" class="h-6 w-6" />
					<XMarkIcon v-else class="h-6 w-6" />
				</button>

				<!-- Logo -->
				<RouterLink to="/" class="w-36">
					<img
						src="@/assets/images/nongmon-logo-white.webp"
						alt="Nongmon Logo"
					/>
				</RouterLink>
			</div>

			<!-- Mobile Navigation Menu -->
			<Transition
				enter-active-class="transition-all duration-300 ease-out"
				enter-from-class="opacity-0 -translate-y-4"
				enter-to-class="opacity-100 translate-y-0"
				leave-active-class="transition-all duration-200 ease-in"
				leave-from-class="opacity-100 translate-y-0"
				leave-to-class="opacity-0 -translate-y-4"
			>
				<div
					v-show="isOpen"
					class="lg:hidden absolute top-24 left-0 right-0 nongmon-red-bg shadow-lg py-4 px-6 z-10 h-[calc(100vh-100px)] w-3/4 sm:w-1/2"
				>
					<ul class="flex flex-col space-y-4 text-white">
						<li>
							<div class="flex items-center justify-between w-full">
								<RouterLink
									:to="{ name: 'home', hash: '#about' }"
									class="hover:opacity-75 transition-opacity"
									@click="closeMenu"
								>
									ABOUT
								</RouterLink>
							</div>
						</li>
						<li>
							<div class="flex items-center justify-between w-full">
								<RouterLink
									:to="{ name: 'home', hash: '#news' }"
									class="hover:opacity-75 transition-opacity"
									@click="closeMenu"
								>
									HOT NEWS
								</RouterLink>
							</div>
						</li>
						<li>
							<div class="flex items-center justify-between w-full">
								<RouterLink
									:to="{ name: 'home', hash: '#must-eat' }"
									class="hover:opacity-75 transition-opacity"
									@click="closeMenu"
								>
									MUST EAT
								</RouterLink>
							</div>
						</li>
						<li class="relative">
							<div class="space-y-2">
								<button
									class="flex items-center gap-2 w-full font-semibold text-white"
									@click="toggleMobileDropdown"
								>
									<span>FOOD & TRAVEL GUIDE</span>
									<svg
										class="w-4 h-4 transition-transform duration-200"
										:class="{ 'rotate-180': isMobileDropdownOpen }"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M19 9l-7 7-7-7"
										/>
									</svg>
								</button>
								<ul
									class="pl-4 space-y-2 overflow-hidden transition-all duration-200"
									:class="{
										'max-h-0 opacity-0': !isMobileDropdownOpen,
										'max-h-24 opacity-100': isMobileDropdownOpen,
									}"
								>
									<li>
										<RouterLink
											:to="{ name: 'eat-at', hash: '#guide' }"
											class="hover:opacity-75 transition-opacity"
											@click="closeMenu"
										>
											FOOD GUIDE
										</RouterLink>
									</li>
									<li>
										<RouterLink
											:to="{ name: 'articles', hash: '#guide' }"
											class="hover:opacity-75 transition-opacity"
											@click="closeMenu"
										>
											TRAVEL GUIDE
										</RouterLink>
									</li>
								</ul>
							</div>
						</li>
					</ul>
				</div>
			</Transition>
		</div>
	</nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { Bars3Icon, XMarkIcon } from "@heroicons/vue/24/outline";
import { useRouter } from "vue-router";

const isOpen = ref(false);
const isMobileDropdownOpen = ref(false);
const isDesktopDropdownOpen = ref(false);
const router = useRouter();

const closeMenu = () => {
	isOpen.value = false;
	isMobileDropdownOpen.value = false;
	isDesktopDropdownOpen.value = false;
};

const toggleMobileDropdown = () => {
	isMobileDropdownOpen.value = !isMobileDropdownOpen.value;
};

const toggleDesktopDropdown = (event) => {
	event.preventDefault();
	isDesktopDropdownOpen.value = !isDesktopDropdownOpen.value;
};

const handleClickOutside = (event) => {
	if (!event.target.closest(".group")) {
		isDesktopDropdownOpen.value = false;
	}
};

onMounted(() => {
	document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
/* Add smooth scrolling to the whole page */
html {
	scroll-behavior: smooth;
}

/* Optional: Add active state styles */
.active {
	opacity: 1;
	font-weight: bold;
}

/* Dropdown transition */
.group:hover .group-hover\:block {
	display: block;
	animation: fadeIn 0.2s ease-in-out;
}

.nongmon-red-bg {
	background-color: #e91c3e;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Add this to prevent the dropdown from closing when moving to submenu */
.group:hover .group-hover\:block,
.group-hover\:block:hover {
	display: none;
}

/* Mobile dropdown animation */
.max-h-0 {
	max-height: 0;
}

.max-h-24 {
	max-height: 6rem;
}

/* Mobile menu animations */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
	transition: all 0.3s ease;
	max-height: 400px;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
	opacity: 0;
	max-height: 0;
}

/* Mobile dropdown animations */
ul[class*="overflow-hidden"] {
	transition: all 0.3s ease-in-out;
}

.max-h-0 {
	max-height: 0;
	opacity: 0;
	transform: translateY(-10px);
}

.max-h-24 {
	max-height: 200px;
	opacity: 1;
	transform: translateY(0);
}

/* Smooth dropdown animation */
.group-hover\:block {
	transition: all 0.3s ease-in-out;
}

.group:hover .group-hover\:block {
	animation: slideDown 0.3s ease forwards;
}

@keyframes slideDown {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Dropdown visibility and transitions */
.invisible {
	visibility: hidden;
	pointer-events: none;
}

.visible {
	visibility: visible;
	pointer-events: auto;
}

/* Remove conflicting hover styles */
.group:hover .group-hover\:block {
	/* Remove these styles */
}

@keyframes fadeIn {
	/* Remove this if not used */
}

/* Keep the background color */
.nongmon-red-bg {
	background-color: #e91c3e;
}

/* ... rest of your styles ... */
</style>
